// Copyright © 2025 The Things Industries B.V.

import React, { useCallback, useMemo } from 'react'
import { defineMessages } from 'react-intl'
import { useSelector } from 'react-redux'
import { createSelector } from 'reselect'

import Select from '@ttn-lw/components/select'
import Icon, { IconCalendarMonth, IconCpu, IconDownload } from '@ttn-lw/components/icon'
import Dropdown from '@ttn-lw/components/dropdown'
import toast from '@ttn-lw/components/toast'
import Button from '@ttn-lw/components/button'

import FetchTable from '@ttn-lw/containers/fetch-table'

import Message from '@ttn-lw/lib/components/message'
import DateTime from '@ttn-lw/lib/components/date-time'

import JSONPayload from '@console/components/events/previews/shared/json-payload'

import PropTypes from '@ttn-lw/lib/prop-types'
import sharedMessages from '@ttn-lw/lib/shared-messages'
import { composeDataUri, downloadDataUriAsFile } from '@ttn-lw/lib/data-uri'

import {
  getStoredApplicationUplinkMessages,
  getStoredDeviceUplinkMessages,
} from '@console/store/actions/application-server'

import {
  selectApplicationStoredMessagesLoading,
  selectDeviceStoredMessagesLoading,
  selectStoredMessages,
} from '@console/store/selectors/application-server.tti'

import style from './message-storage-table.tti.styl'

const m = defineMessages({
  allDevicesStoredMessages: 'All devices stored messages',
  deviceStoredMessages: "{deviceName}'s stored messages",
  downloadJson: 'Download JSON',
  downloadCsv: 'Download CSV',
  storedMessagesJsonFailed: 'Failed to download stored_messages.json',
  storedMessagesJsonFailedMessage:
    'An unknown error occurred and the stored_messages.json could not be downloaded',
  storedMessagesCsvFailed: 'Failed to download stored_messages.csv',
  storedMessagesCsvFailedMessage:
    'An unknown error occurred and the stored_messages.csv could not be downloaded',
})

const rowKeySelector = row => `${row.end_device_ids.device_id}/${row.received_at}`

const MessageStorageTable = ({
  appId,
  selectedDevice,
  date,
  handleDeviceIdChange,
  handleDateChange,
  dateOptions,
  deviceIdOptions,
}) => {
  const applicationStoredMessagesLoading = useSelector(selectApplicationStoredMessagesLoading)
  const deviceStoredMessagesLoading = useSelector(selectDeviceStoredMessagesLoading)
  const storedMessages = useSelector(selectStoredMessages)

  const selectedDate = useMemo(
    () => dateOptions.find(d => d.value === date) ?? '',
    [date, dateOptions],
  )

  const headers = React.useMemo(
    () => [
      {
        name: 'end_device_ids.device_id',
        displayName: sharedMessages.devID,
        width: '15rem',
      },
      {
        name: 'received_at',
        displayName: sharedMessages.received,
        render: received_at => <DateTime value={received_at} />,
        width: '12rem',
      },
      {
        name: 'uplink_message.f_port',
        displayName: sharedMessages.fPort,
        align: 'center',
        width: '8rem',
      },
      {
        name: 'uplink_message.f_cnt',
        displayName: sharedMessages.fCnt,
        align: 'center',
        width: '8rem',
      },
      {
        name: 'uplink_message.rx_metadata',
        displayName: sharedMessages.seenByGateways,
        render: rxMetadata => rxMetadata.length ?? 0,
        align: 'center',
        width: '15rem',
      },
      {
        name: 'uplink_message.decoded_payload',
        displayName: sharedMessages.decodedPayload,
        render: decodedPayload => <JSONPayload data={decodedPayload ?? ''} />,
        align: 'left',
        width: 'fit-content',
      },
    ],
    [],
  )
  const getItemsAction = useCallback(
    () =>
      selectedDevice.id === 'all'
        ? getStoredApplicationUplinkMessages(appId, 10, date)
        : getStoredDeviceUplinkMessages(appId, selectedDevice.id, 10, date),
    [appId, date, selectedDevice.id],
  )

  const baseDataSelector = createSelector(
    state => selectStoredMessages(state),
    storedMessages => ({
      storedMessages,
      totalCount: storedMessages?.length ?? 0,
      mayAdd: false,
      mayLink: false,
    }),
  )

  const handleDownloadJson = useCallback(() => {
    try {
      const storedMessagesDataUri = composeDataUri(JSON.stringify(storedMessages, undefined, 2))
      downloadDataUriAsFile(storedMessagesDataUri, `stored_messages_${Date.now()}.json`)
    } catch (err) {
      toast({
        title: m.storedMessagesJsonFailed,
        message: m.storedMessagesJsonFailedMessage,
        type: toast.types.ERROR,
      })
    }
  }, [storedMessages])

  const handleDownloadCsv = useCallback(() => {
    try {
      const csvHeaders = headers.map(h => h.displayName.defaultMessage)
      const transformedData = storedMessages.map(item => ({
        device_id: item.end_device_ids.device_id,
        received_at: new Date(item.received_at)
          .toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hourCycle: 'h23',
          })
          .replace(/^([^,]+,[^,]+),/, '$1'), // ← Removes *only* the second comma to match date format in table
        f_port: item.uplink_message.f_port ?? '',
        f_cnt: item.uplink_message.f_cnt ?? '',
        seen_by_gateways: item.uplink_message.rx_metadata.length ?? 0,
        decoded_payload: item.uplink_message.decoded_payload
          ? JSON.stringify(item.uplink_message.decoded_payload)
          : '',
      }))

      const csvRows = [
        csvHeaders.join(','),
        ...transformedData.map(obj =>
          Object.values(obj)
            .map(value => `"${`${value}`.replace(/"/g, '""')}"`)
            .join(','),
        ),
      ]
      const csvString = csvRows.join('\n')
      const blob = new Blob([csvString], { type: 'text/csv' })
      const storedMessagesDataUri = URL.createObjectURL(blob)
      downloadDataUriAsFile(storedMessagesDataUri, `stored_messages_${Date.now()}.csv`)
    } catch (err) {
      toast({
        title: m.storedMessagesCsvFailed,
        message: m.storedMessagesCsvFailedMessage,
        type: toast.types.ERROR,
      })
    }
  }, [headers, storedMessages])

  const dropdownItems = (
    <>
      <Dropdown.Item title={m.downloadJson} action={handleDownloadJson} icon={IconDownload} />
      <Dropdown.Item title={m.downloadCsv} action={handleDownloadCsv} icon={IconDownload} />
    </>
  )

  return (
    <div className="d-flex flex-column gap-ls-xs">
      <div className="d-flex gap-cs-s">
        <Select
          name="messages-select"
          options={deviceIdOptions}
          value={selectedDevice.id}
          onChange={handleDeviceIdChange}
          inputWidth="m"
          disabled={applicationStoredMessagesLoading || deviceStoredMessagesLoading}
        />
        <Select
          name="date-select"
          options={dateOptions}
          value={date}
          onChange={handleDateChange}
          inputWidth="s"
          disabled={applicationStoredMessagesLoading || deviceStoredMessagesLoading}
        />
      </div>

      <div className={style.tableWrapper}>
        <div className={style.tableHeader}>
          <div className="d-flex gap-cs-xxs al-center">
            <Icon icon={IconCpu} />
            {selectedDevice.id === 'all' ? (
              <Message content={m.allDevicesStoredMessages} className="fw-bold" />
            ) : (
              <Message
                content={m.deviceStoredMessages}
                values={{
                  deviceName: selectedDevice.name,
                }}
                className="fw-bold"
              />
            )}
          </div>
          <div className="d-flex gap-cs-xxs al-center">
            <Icon icon={IconCalendarMonth} className="c-icon-neutral-normal" />
            <Message
              content={selectedDate.label}
              values={selectedDate.labelValues}
              className="c-text-neutral-semilight"
            />
          </div>
        </div>
        <div className={style.tableBackground}>
          <FetchTable
            rowKeySelector={rowKeySelector}
            entity="storedMessages"
            defaultOrder="-received_at"
            clickable={false}
            headers={headers}
            getItemsAction={getItemsAction}
            baseDataSelector={baseDataSelector}
            headerClassName={style.tableSubHeader}
            smallCells
            panelStyle
            tableClassName={style.table}
          />
        </div>
      </div>
      <Button
        message={sharedMessages.download}
        secondary
        dropdownItems={dropdownItems}
        dropdownPosition="below right"
      />
    </div>
  )
}

MessageStorageTable.propTypes = {
  appId: PropTypes.string.isRequired,
  date: PropTypes.string.isRequired,
  dateOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.message.isRequired,
      labelValues: PropTypes.object,
    }),
  ).isRequired,
  deviceIdOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.message.isRequired,
    }),
  ).isRequired,
  handleDateChange: PropTypes.func.isRequired,
  handleDeviceIdChange: PropTypes.func.isRequired,
  selectedDevice: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  }).isRequired,
}

export default MessageStorageTable
